import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  TextField,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';
import {
  Add as AddIcon,
  Close as CloseIcon,
} from '@material-ui/icons';
import { ReactSortable } from 'react-sortablejs';
import { useHistory } from 'react-router-dom';

import { PHONE_TYPES, USA_STATES } from 'common/constants';
import { selectBrandOwners } from 'selectors/brandRegistration';
import {
  addOwner,
  setOwnerInfo,
  setOwnersOrder,
  removeOwner,
  createOwners,
  deleteOwner,
} from 'modules/brandRegistration';
import {COUNTIES, isAdmin} from 'util/APIUtils';
import { formatPhoneNumber } from 'util/helpers';
import FormValidator from 'components/formValidator';
import TextMaskPhone from 'components/TextMaskPhone';
import {
  useFormStyles,
  useOwnerStyles,
} from '../styles';
import { createId } from '../util';
import {ADMIN_OWNER_DESCRIPTOR, OWNER_DESCRIPTOR} from '../constants';
import StepActions from './StepActions';
import {selectUser} from "../../../selectors/user";

const BrandOwners = () => {
  const dispatch = useDispatch();
  const owners = useSelector(selectBrandOwners);
  const addNewOwner = (...args) => dispatch(addOwner(...args));
  const classes = useOwnerStyles();
  const formClasses = useFormStyles();
  const history = useHistory();
  const user = useSelector(selectUser);

  const buildInput = ({
    id,
    index,
    value,
    label,
    setFormValue,
    error,
    setFormValues,
    connectedField,
    inputProps,
  }) => {
    return (
      <TextField
        id={`${id}${index}`}
        value={value}
        label={label}
        variant="outlined"
        size="small"
        onChange={(ev) => {
          if (connectedField && setFormValues) {
            setFormValues({
              [id]: ev.target.value,
              [connectedField]: ev.target.value,
            });
          } else {
            setFormValue(id, ev.target.value);
          }
        }}
        error={!!error}
        helperText={error}
        InputProps={inputProps}
      />
    );
  };

  const buildSelect = ({
    options,
    index,
    id,
    label,
    value,
    setFormValue,
    setFormValues,
    connectedField,
    error,
    keyValue = false,
  }) => {
    return (
      <FormControl
        variant="outlined"
        size="small"
        error={!!error}
      >
        <InputLabel htmlFor={`${id}${index}`}>{label}</InputLabel>
        <Select
          native
          value={value}
          id={`${id}${index}`}
          label={label}
          onChange={(ev) => {
            if (connectedField && setFormValues) {
              setFormValues({
                [id]: ev.target.value,
                [connectedField]: ev.target.value,
              });
            } else {
              setFormValue(id, ev.target.value);
            }
          }}
        >
          <option aria-label="None" value="" />
          {options instanceof Array ?
            (
              options.map((county) =>
                <option key={county} value={county}>{county}</option>
              )
            ) : (
              Object.entries(options).map(([key, type]) =>
                <option key={key} value={keyValue ? key : type}>{type}</option>
              )
            )
          }
        </Select>
        {!!error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    );
  }

  const handleOwnerFormSubmit = (ev, { errors, formData }, index) => {
    ev.preventDefault();

    if (!errors) {
      dispatch(setOwnerInfo({ ...formData, valid: true, ownerNumber: index + 1 }, index));

      if (index + 1 === owners.length) {
        dispatch(createOwners());
      }
    } else {
      dispatch(setOwnerInfo({ ...formData, valid: false, ownerNumber: index + 1 }, index));
    }
  };

  const buildItem = (item, index) => {
    return (
      <FormValidator
        initialData={{
          ...item,
          phoneNumber: !!item.phoneNumber ? formatPhoneNumber(item.phoneNumber) : '',
        }}
        descriptor={isAdmin(user) ? ADMIN_OWNER_DESCRIPTOR : OWNER_DESCRIPTOR}
        onSubmit={(ev, result) => handleOwnerFormSubmit(ev, result, index)}
        key={`owner-form-${item.childId}`}
      >
        {({ formData, setFormValue, setFormValues, handleSubmit, errors = {} }) => {
          const hasName = !!formData.firstName || !!formData.lastName;
          const isExisting = !!formData.id;

          return (
            <div className={formClasses.draggable}>
              <span className="owner-handle" />
              <div className={classes.form}>
                <div className={classes.formTitle}>
                  <h3><strong>{index + 1}.</strong> {hasName ? `${formData.firstName} ${formData.lastName}` : <i>Unassigned</i>}</h3>
                  {owners.length > 1 && (
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => {
                        if (isExisting) {
                          dispatch(deleteOwner(formData.id, index));
                        } else {
                          dispatch(removeOwner(index));
                        }
                      }}
                    >
                      <CloseIcon /> Remove
                    </Button>
                  )}
                </div>
                <form
                  className={formClasses.root}
                  onSubmit={handleSubmit}
                >
                  <div className="row row-3">
                    {buildInput({ index, id: 'businessName', label: 'Business Name', value: formData.businessName, setFormValue, error: errors.businessName })}
                    {buildInput({ index, id: 'firstName', label: 'First Name', value: formData.firstName, setFormValue, error: errors.firstName })}
                    {buildInput({ index, id: 'lastName', label: 'Last Name', value: formData.lastName, setFormValue, error: errors.lastName })}
                  </div>
                  <div className="row row-3">
                    {buildInput({ index, id: 'email', label: 'Email', value: formData.email, type: 'email', setFormValue, error: errors.email })}
                    {buildSelect({ index, id: 'phoneType', label: 'Phone Type', value: formData.phoneType, options: PHONE_TYPES, setFormValue, error: errors.phoneType })}
                    {buildInput({
                      index,
                      id: 'phoneNumber',
                      label: 'Phone Number',
                      value: formData.phoneNumber,
                      setFormValue,
                      error: errors.phoneNumber,
                      inputProps: { inputComponent: TextMaskPhone },
                    })}
                  </div>
                  <div className="row row-1">
                    <div>
                      <h4>Street Address</h4>
                    </div>
                  </div>
                  <div className="row row-2">
                    {buildInput({
                      index,
                      id: 'lineOne',
                      label: 'Line One',
                      value: formData.lineOne,
                      setFormValue,
                      setFormValues,
                      error: errors.lineOne,
                      connectedField: formData.isSameAddresses ? 'mailingLineOne' : '',
                    })}
                    {buildInput({
                      index,
                      id: 'lineTwo',
                      label: 'Line Two',
                      value: formData.lineTwo,
                      setFormValue,
                      setFormValues,
                      error: errors.lineTwo,
                      connectedField: formData.isSameAddresses ? 'mailingLineTwo' : '',
                    })}
                  </div>
                  <div className="row row-4">
                    {buildInput({
                      index,
                      id: 'city',
                      label: 'City',
                      value: formData.city,
                      setFormValue,
                      setFormValues,
                      error: errors.city,
                      connectedField: formData.isSameAddresses ? 'mailingCity' : '',
                    })}
                    {buildSelect({
                      index,
                      id: 'state',
                      label: 'State',
                      value: formData.state,
                      options: USA_STATES,
                      setFormValue,
                      error: errors.state,
                      keyValue: true,
                      connectedField: formData.isSameAddresses ? 'mailingState' : '',
                    })}
                    {buildInput({
                      index,
                      id: 'zipcode',
                      label: 'Zip Code',
                      value: formData.zipcode,
                      setFormValue,
                      setFormValues,
                      error: errors.zipcode,
                      connectedField: formData.isSameAddresses ? 'mailingZipcode' : '',
                    })}
                    {buildSelect({
                      index,
                      id: 'county',
                      label: 'County',
                      value: formData.county,
                      options: COUNTIES,
                      setFormValue,
                      setFormValues,
                      error: errors.county,
                      connectedField: formData.isSameAddresses ? 'mailingCounty' : '',
                    })}
                  </div>
                  <div className="row row-1">
                    <div>
                      <h4>Mailing Address</h4>
                    </div>
                    <div>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="isSameAddresses"
                            checked={formData.isSameAddresses}
                            onChange={(event) => {
                              if (event.target.checked) {
                                setFormValues({
                                  mailingLineOne: formData?.lineOne,
                                  mailingLineTwo: formData?.lineTwo,
                                  mailingCity: formData?.city,
                                  mailingState: formData?.state,
                                  mailingZipcode: formData?.zipcode,
                                  mailingCounty: formData?.county,
                                  isSameAddresses: true,
                                });
                              } else {
                                setFormValues({
                                  mailingLineOne: item?.mailingLineOne || '',
                                  mailingLineTwo: item?.mailingLineTwo || '',
                                  mailingCity: item?.mailingCity || '',
                                  mailingState: item?.mailingState || 'UT',
                                  mailingZipcode: item?.mailingZipcode || '',
                                  mailingCounty: item?.mailingCounty || '',
                                  isSameAddresses: false,
                                });
                              }
                            }}
                            color="primary"
                          />
                        }
                        label="Same as street address"
                      />
                    </div>
                  </div>
                  <div style={{ display: formData.isSameAddresses ? 'none' : 'block' }}>
                    <div className="row row-2">
                      {buildInput({ index, id: 'mailingLineOne', label: 'Line One', value: formData.mailingLineOne, setFormValue, error: errors.mailingLineOne })}
                      {buildInput({ index, id: 'mailingLineTwo', label: 'Line Two', value: formData.mailingLineTwo, setFormValue, error: errors.mailingLineTwo })}
                    </div>
                    <div className="row row-4">
                      {buildInput({ index, id: 'mailingCity', label: 'City', value: formData.mailingCity, setFormValue, error: errors.mailingCity })}
                      {buildSelect({ index, id: 'mailingState', label: 'State', value: formData.mailingState, options: USA_STATES, setFormValue, error: errors.mailingState, keyValue: true })}
                      {buildInput({ index, id: 'mailingZipcode', label: 'Zip Code', value: formData.mailingZipcode, setFormValue, error: errors.mailingZipcode })}
                      {buildSelect({ index, id: 'mailingCounty', label: 'County', value: formData.mailingCounty, options: COUNTIES, setFormValue, error: errors.mailingCounty })}
                    </div>
                  </div>
                </form>
              </div>
            </div>
          );
        }}
      </FormValidator>
    );
  };

  const handleSort = (newList) => {
    dispatch(setOwnersOrder(newList.map((o, index) => ({ ...o, ownerNumber: index + 1 }))));
  };

  const handleNext = (ev) => {
    ev.preventDefault();

    const forms = document.querySelectorAll('#ownersForm form');

    [].forEach.call(forms, (form) => {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
  };

  const handleBack = () => {
    history.push('/brands/new/positions');
  };

  return (
    <div className={classes.wrapper} id="ownersForm">
      <div className={classes.heading} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3>Add Brand Owners</h3>
          <p><i>NOTE: Hold the bar on the left of the form to drag and drop to re-order owners.</i></p>
        </div>
        <div>
          <Button variant="contained" color="primary" onClick={addNewOwner}>
            <AddIcon /> Add owner
          </Button>
        </div>
        <div>
          <StepActions handleBack={handleBack} handleNext={handleNext} />
        </div>
      </div>
      <ReactSortable
        list={owners}
        setList={handleSort}
        handle=".owner-handle"
        clone={item => ({ ...item, id: createId() })}
      >
        {owners.map(buildItem)}
      </ReactSortable>
      <StepActions handleBack={handleBack} handleNext={handleNext} />
    </div>
  );
};

export default BrandOwners;
